/** Base DNS zone for local Cloudflare tunnel hosts (without per-user label). */
export const LOCAL_TUNNEL_BASE_DOMAIN = 'local.perpetualpress.io';

/** OS users that keep legacy hosts: `{service}.local.perpetualpress.io`. */
export const LEGACY_TUNNEL_USERS = Object.freeze(['azureuser', 'ec2-user']);

/**
 * @param {Record<string, string | undefined>} [env]
 * @returns {Record<string, string | undefined>}
 */
function readEnv(env) {
  if (env) return env;
  const vite =
    typeof import.meta !== 'undefined' && import.meta.env && typeof import.meta.env === 'object'
      ? import.meta.env
      : {};
  return {
    ...vite,
    ...(typeof process !== 'undefined' && process.env ? process.env : {}),
  };
}

/**
 * Sanitize a username for DNS labels.
 * @param {string} raw
 * @returns {string}
 */
export function sanitizeTunnelUser(raw) {
  const cleaned = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32);
  return cleaned || 'dev';
}

/**
 * @param {Record<string, string | undefined>} [env]
 * @returns {string}
 */
export function resolveLocalTunnelUser(env) {
  const e = readEnv(env);
  const raw =
    e.PB_TUNNEL_USER ||
    e.VITE_PB_TUNNEL_USER ||
    e.USER ||
    e.USERNAME ||
    '';
  return sanitizeTunnelUser(raw);
}

/**
 * @param {Record<string, string | undefined>} [env]
 * @returns {boolean}
 */
export function isLegacyTunnelUser(env) {
  const user = resolveLocalTunnelUser(env);
  return LEGACY_TUNNEL_USERS.includes(user);
}

/**
 * Effective tunnel zone used in `{service}.{zone}`.
 * Respects explicit LOCAL_TUNNEL_ZONE; otherwise legacy users keep the base domain,
 * and everyone else gets `{user}.local.perpetualpress.io`.
 *
 * @param {Record<string, string | undefined>} [env]
 * @returns {string}
 */
export function resolveLocalTunnelZone(env) {
  const e = readEnv(env);
  const explicit = String(e.LOCAL_TUNNEL_ZONE || e.VITE_LOCAL_TUNNEL_ZONE || '').trim();
  if (explicit) return explicit;

  if (isLegacyTunnelUser(e)) {
    return LOCAL_TUNNEL_BASE_DOMAIN;
  }

  const user = resolveLocalTunnelUser(e);
  return `${user}.${LOCAL_TUNNEL_BASE_DOMAIN}`;
}

/**
 * @deprecated Prefer resolveLocalTunnelZone() — value is resolved at call time via buildTunnelHost.
 * Kept for call sites that imported the old constant; resolves once from current env at module load.
 */
export const LOCAL_TUNNEL_ZONE = resolveLocalTunnelZone();

/**
 * @param {string} service nf | site-api | poln-api | connect-api | widget | mcp
 * @param {Record<string, string | undefined>} [env]
 */
export function buildTunnelHost(service, env) {
  return `${service}.${resolveLocalTunnelZone(env)}`;
}

/**
 * @param {string} service
 * @param {Record<string, string | undefined>} [env]
 */
export function buildTunnelUrl(service, env) {
  return `https://${buildTunnelHost(service, env)}`;
}

export function localTunnelCfgEnabled(env) {
  const e = readEnv(env);
  if (e.PB_LOCAL_TUNNEL === '0' || e.PB_LOCAL_TUNNEL === 'false') {
    return false;
  }
  return Boolean(e.NF_WEB_BASEURL || e.PB_USE_LOCAL_TUNNEL === '1');
}

export function applyLocalTunnelCfg(cfg, env) {
  if (!localTunnelCfgEnabled(env)) {
    return cfg;
  }

  const connectUrl = buildTunnelUrl('connect-api', env);
  const nfUrl = buildTunnelUrl('nf', env);
  const polnUrl = buildTunnelUrl('poln-api', env);
  const siteUrl = buildTunnelUrl('site-api', env);
  const mcpUrl = buildTunnelUrl('mcp', env);

  if (cfg.connect) {
    cfg.connect.baseUrl = connectUrl;
  }
  if (cfg.app?.nf?.web) {
    cfg.app.nf.web.baseUrl = nfUrl;
  }
  if (cfg.siteApi) {
    cfg.siteApi.baseUrl = siteUrl;
  }
  if (cfg.mcp?.eval) {
    cfg.mcp.eval.baseUrl = mcpUrl;
  }

  let wsEndpoint = `wss://${buildTunnelHost('connect-api', env)}`;
  if (cfg.svcProfile) {
    wsEndpoint = `${wsEndpoint}/${cfg.svcProfile}`;
  }
  if (cfg.app?.nf) {
    cfg.app.nf.ws_endpoint = wsEndpoint;
  }

  return cfg;
}
